import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import PersonalInfoSection from './components/PersonalInfoSection';
import MembershipTierPanel from './components/MembershipTierPanel';
import NotificationSettings from './components/NotificationSettings';
import AchievementShowcase from './components/AchievementShowcase';
import ReferralProgram from './components/ReferralProgram';
import AccountSecurity from './components/AccountSecurity';

const MemberProfile = () => {
  const [activeTab, setActiveTab] = useState('personal');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [user, setUser] = useState(null);
  const [notifications, setNotifications] = useState([]);

  // Mock user data
  const mockUser = {
    id: 1,
    name: "Alex Johnson",
    email: "alex.johnson@email.com",
    phone: "+1 (555) 123-4567",
    dateOfBirth: "1990-05-15",
    gender: "male",
    location: "New York, NY",
    emergencyContact: "Sarah Johnson",
    emergencyPhone: "+1 (555) 987-6543",
    tier: "All Access",
    tierProgress: 75,
    joinDate: "March 2024",
    avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face"
  };

  // Mock notifications
  const mockNotifications = [
    {
      id: 1,
      title: "Profile Update",
      message: "Your profile information has been successfully updated",
      time: "2 hours ago",
      read: false
    },
    {
      id: 2,
      title: "New Achievement",
      message: "You\'ve earned the \'Consistency Master\' badge!",
      time: "1 day ago",
      read: false
    },
    {
      id: 3,
      title: "Referral Reward",
      message: "You\'ve earned $50 from a successful referral",
      time: "3 days ago",
      read: true
    }
  ];

  useEffect(() => {
    setUser(mockUser);
    setNotifications(mockNotifications);
  }, []);

  const tabs = [
    { id: 'personal', label: 'Personal Info', icon: 'User' },
    { id: 'membership', label: 'Membership', icon: 'Award' },
    { id: 'notifications', label: 'Notifications', icon: 'Bell' },
    { id: 'achievements', label: 'Achievements', icon: 'Trophy' },
    { id: 'referrals', label: 'Referrals', icon: 'Users' },
    { id: 'security', label: 'Security', icon: 'Shield' }
  ];

  const handlePersonalInfoSave = (formData) => {
    setUser(prev => ({ ...prev, ...formData }));
    // Mock API call
    console.log('Saving personal info:', formData);
  };

  const handleMembershipUpgrade = (tierName) => {
    // Mock upgrade process
    console.log('Upgrading to:', tierName);
    // In real app, this would redirect to payment or show upgrade modal
  };

  const handleNotificationSettingsSave = (settings) => {
    // Mock save notification settings
    console.log('Saving notification settings:', settings);
  };

  const handleAchievementShare = (achievement) => {
    // Mock share functionality
    console.log('Sharing achievement:', achievement);
  };

  const handleReferralInvite = (inviteData) => {
    // Mock invite functionality
    console.log('Sending referral invite:', inviteData);
  };

  const handleSecurityUpdate = (updateData) => {
    // Mock security update
    console.log('Security update:', updateData);
  };

  const handleNotificationClick = (notification) => {
    // Mark notification as read
    setNotifications(prev => 
      prev?.map(n => n?.id === notification?.id ? { ...n, read: true } : n)
    );
  };

  const handleProfileClick = () => {
    // Already on profile page
    console.log('Profile clicked');
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'personal':
        return (
          <PersonalInfoSection
            user={user}
            onSave={handlePersonalInfoSave}
          />
        );
      case 'membership':
        return (
          <MembershipTierPanel
            user={user}
            onUpgrade={handleMembershipUpgrade}
          />
        );
      case 'notifications':
        return (
          <NotificationSettings
            settings={{}}
            onSave={handleNotificationSettingsSave}
          />
        );
      case 'achievements':
        return (
          <AchievementShowcase
            achievements={[]}
            onShare={handleAchievementShare}
          />
        );
      case 'referrals':
        return (
          <ReferralProgram
            referralData={null}
            onInvite={handleReferralInvite}
          />
        );
      case 'security':
        return (
          <AccountSecurity
            securityData={null}
            onUpdate={handleSecurityUpdate}
          />
        );
      default:
        return null;
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header
        user={user}
        notifications={notifications}
        onNotificationClick={handleNotificationClick}
        onProfileClick={handleProfileClick}
      />
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        user={user}
      />
      <main className={`transition-all duration-300 ${
        isSidebarCollapsed ? 'ml-16' : 'ml-64'
      } pt-4`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 rounded-full overflow-hidden bg-muted">
                <img
                  src={user?.avatar}
                  alt="Profile"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = '/assets/images/no_image.png';
                  }}
                />
              </div>
              <div>
                <h1 className="text-3xl font-heading font-bold text-foreground">
                  {user?.name}
                </h1>
                <p className="text-muted-foreground">{user?.email}</p>
                <div className="flex items-center space-x-2 mt-2">
                  <div className="w-2 h-2 bg-accent rounded-full animate-breathing"></div>
                  <span className="text-sm font-caption text-accent-foreground font-medium">
                    {user?.tier} Member since {user?.joinDate}
                  </span>
                </div>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="border-b border-border">
              <nav className="flex space-x-8 overflow-x-auto">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-wellness ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                    }`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {tab?.icon === 'User' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />}
                      {tab?.icon === 'Award' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />}
                      {tab?.icon === 'Bell' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />}
                      {tab?.icon === 'Trophy' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />}
                      {tab?.icon === 'Users' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />}
                      {tab?.icon === 'Shield' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />}
                    </svg>
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="pb-8">
            {renderTabContent()}
          </div>
        </div>
      </main>
    </div>
  );
};

export default MemberProfile;